/**
 * Triage-Vorschlaege am Ticket (Spec 9.3).
 *
 * Jeder Vorschlag ist **als KI-Vorschlag gekennzeichnet** (Icon + Text) und
 * hat zwei Knoepfe: annehmen oder ablehnen. Ohne Klick passiert nichts — die
 * API wendet nichts von selbst an.
 *
 * Was hier gar nicht erst ankommt, filtert der Server: Vorschlaege zu
 * Feldern, die ein Mensch inzwischen selbst gesetzt hat, liefert er nicht aus.
 */
import type { AiSuggestion, Queue, Tag } from '@tickethub/shared';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type AiTriageBannerProps = {
  suggestions: AiSuggestion[];
  tags: Tag[];
  queues: Queue[];
  disabled: boolean;
  onAccept: (suggestion: AiSuggestion) => void;
  onReject: (suggestion: AiSuggestion) => void;
};

export function AiTriageBanner({
  suggestions,
  tags,
  queues,
  disabled,
  onAccept,
  onReject,
}: AiTriageBannerProps) {
  const { t } = useTranslation();
  if (suggestions.length === 0) return null;

  /** Der Vorschlag in Worten — IDs helfen niemandem beim Entscheiden. */
  function describe(suggestion: AiSuggestion): string {
    const value = suggestion.value;
    if (value.field === 'priority') return t(`ticket.priority_${value.priority}`);
    if (value.field === 'queue') {
      return queues.find((queue) => queue.id === value.queue_id)?.name ?? value.queue_id;
    }
    return value.tag_ids
      .map((id) => tags.find((tag) => tag.id === id)?.name ?? id)
      .join(', ');
  }

  return (
    <section
      className="space-y-3 rounded-lg border border-violet-400/60 bg-violet-50/50 p-4 dark:bg-violet-950/20"
      aria-label={t('ai.triage_title')}
    >
      <div className="flex items-center gap-2">
        <Sparkles aria-hidden="true" className="size-4" />
        <h2 className="text-sm font-medium">{t('ai.triage_title')}</h2>
        <Badge variant="secondary">{t('ai.badge')}</Badge>
      </div>

      <ul className="space-y-2">
        {suggestions.map((suggestion) => (
          <li key={suggestion.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{t(`ai.triage_field_${suggestion.field}`)}:</span>
            <span>{describe(suggestion)}</span>
            {suggestion.reason ? (
              <span className="text-xs text-muted-foreground">— {suggestion.reason}</span>
            ) : null}
            <span className="ml-auto flex gap-2">
              <Button size="sm" disabled={disabled} onClick={() => onAccept(suggestion)}>
                {t('ai.triage_accept')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => onReject(suggestion)}
              >
                {t('ai.triage_reject')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
